import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  BookingPhoneListResponse,
  BookingPhoneSaveRequest,
  BookingPhoneSaveResult,
} from '@guilleumes/types';

import { api } from '@/services/api';

/**
 * «Precontacte Booking» (`plans/booking-phone-recovery.md` §5.2 i fitxa F, punt 2).
 *
 * Booking ja no envia el telèfon per API: aquí es llegeix la llista de reserves de Booking sense
 * telèfon i es desa el que una persona ha copiat de l'extranet. Res d'aquí parla amb l'extranet.
 */

export const precontactBookingKeys = {
  all: ['precontact-booking'] as const,
  list: () => [...precontactBookingKeys.all, 'list'] as const,
  count: () => [...precontactBookingKeys.all, 'count'] as const,
};

/**
 * El que es mou amb qualsevol mutació d'aquesta pàgina: les llistes i el comptador del menú, i la
 * graella del calendari (el llum de contacte de la card canvia quan la reserva guanya telèfon, i
 * ensenya el ☎ «cal trucar» quan es marca que no té WhatsApp).
 */
function invalidateAfterChange(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: precontactBookingKeys.all });
  queryClient.invalidateQueries({ queryKey: ['calendar-bookings'] });
}

/**
 * Missatge d'un error d'API, apte per ensenyar tal qual. El backend (Nest) el posa a
 * `response.data.message`: un text, o una llista quan és `class-validator` qui rebutja el cos.
 */
export function bookingApiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (Array.isArray(message)) {
    const texts = message.filter((item): item is string => typeof item === 'string');
    if (texts.length > 0) return texts.join('. ');
  }
  if (typeof message === 'string' && message.trim()) return message;
  return error instanceof Error && error.message ? error.message : fallback;
}

/** Codi HTTP d'un error d'`api` (`ApiError.response.status`), o `null` si no n'és (xarxa caiguda). */
export function bookingApiErrorStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
}

/** Les quatre llistes: sense telèfon, per escriure, ja escrites i «Booking no en té». */
export function usePrecontactBooking() {
  return useQuery<BookingPhoneListResponse>({
    queryKey: precontactBookingKeys.list(),
    queryFn: async () => {
      const response = await api.get('/precontact-booking');
      return response.data;
    },
    // Llista de treball: les reserves noves hi entren pel sync, no cal rellegir-la cada pocs segons.
    staleTime: 60 * 1000,
  });
}

/** Comptador del menú lateral: files sense telèfon del primer tram (propers 7 dies). */
export function usePrecontactBookingCount(): number {
  const { data } = useQuery<{ count: number }>({
    queryKey: precontactBookingKeys.count(),
    queryFn: async () => {
      const response = await api.get('/precontact-booking/count');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  return data?.count ?? 0;
}

/**
 * Desa el telèfon enganxat. El servidor el normalitza, l'escriu **primer a Beds24** i només llavors
 * a la fitxa; en un grup, a totes les habitacions sense telèfon. Un 400 porta el perquè al missatge.
 */
export function useSaveBookingPhone() {
  const queryClient = useQueryClient();

  return useMutation<
    BookingPhoneSaveResult,
    Error,
    { reservationId: string } & BookingPhoneSaveRequest
  >({
    mutationFn: async ({ reservationId, phone }) => {
      const response = await api.patch(`/precontact-booking/${reservationId}/phone`, { phone });
      return response.data;
    },
    // També quan `saved` és fals, hi ha `failed` o la petició peta: en un grup pot haver-se desat
    // una part, i la llista ha d'ensenyar el que hi ha de debò abans de tornar-ho a provar.
    onSettled: () => invalidateAfterChange(queryClient),
  });
}

/** «Booking no en té»: treu la fila de la llista. Amb `undo`, la hi torna. No toca Beds24. */
export function useBookingNoPhone() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: true }, Error, { reservationId: string; undo?: boolean }>({
    mutationFn: async ({ reservationId, undo }) => {
      const url = `/precontact-booking/${reservationId}/no-phone`;
      const response = undo ? await api.delete(url) : await api.put(url);
      return response.data;
    },
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/**
 * «Ja li he escrit»: treu la fila de «Per escriure». Amb `undo`, la hi torna: és també el «Torna-la
 * a la llista» del bloc «Ja escrites» (§13.3), que a més torna a pendents la benvinguda enviada.
 */
export function useBookingWritten() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: true }, Error, { reservationId: string; undo?: boolean }>({
    mutationFn: async ({ reservationId, undo }) => {
      const url = `/precontact-booking/${reservationId}/written`;
      const response = undo ? await api.delete(url) : await api.put(url);
      return response.data;
    },
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}

/**
 * «No té WhatsApp» (§13.3): la fila es queda a «Per escriure» fins que se li hagi trucat, encara
 * que la benvinguda consti com a enviada. Amb `undo` («Sí que en té»), la marca es treu. No toca
 * Beds24.
 */
export function useBookingNoWhatsapp() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: true }, Error, { reservationId: string; undo?: boolean }>({
    mutationFn: async ({ reservationId, undo }) => {
      const url = `/precontact-booking/${reservationId}/no-whatsapp`;
      const response = undo ? await api.delete(url) : await api.put(url);
      return response.data;
    },
    onSuccess: () => invalidateAfterChange(queryClient),
  });
}
